import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) navigate("/admin", { replace: true });
  }, [token, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      login(data.access_token, data.email);
      toast.success("Welcome back!");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Login failed"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center px-5 py-16" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-ink mb-6">
          <ArrowLeft size={14} /> Back to site
        </Link>
        <div className="bg-white border border-brand-border rounded-3xl p-8 shadow-sm">
          <div className="w-12 h-12 rounded-xl bg-brand-soft grid place-items-center text-brand-primary">
            <Lock size={20} />
          </div>
          <h1 className="mt-5 font-heading text-2xl font-medium">Admin Sign In</h1>
          <p className="text-sm text-brand-muted mt-1">
            Access your Career Connect leads dashboard.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <div>
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-11 rounded-xl border-brand-border bg-white"
                placeholder="admin@careerconnect.in"
                data-testid="admin-login-email"
              />
            </div>
            <div>
              <Label htmlFor="login-password">Password</Label>
              <Input
                id="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-11 rounded-xl border-brand-border bg-white"
                placeholder="Your password"
                data-testid="admin-login-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full h-11 bg-brand-primary text-white hover:bg-brand-primaryHover"
              data-testid="admin-login-submit"
            >
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
